import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Play, RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminPlatformScheduler() {
  const [st, setSt] = useState(null);
  const [running, setRunning] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = () => apiClient.get("/scheduler/platform-status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runJob = async (id) => {
    setRunning(id);
    try {
      const r = await apiClient.post(`/scheduler/platform-run/${id}`, null, { timeout: 300000 });
      const res = r.data.runs?.[0];
      res?.ok ? toast.success(`${id} ausgeführt (${res.duration_s} s)`) : toast.error(`${id}: ${res?.error || "fehlgeschlagen"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Ausführung fehlgeschlagen"); }
    finally { setRunning(null); }
  };

  if (!st) return null;
  const jobs = showAll ? st.jobs : st.jobs.slice(0, 8);
  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm mt-4" data-testid="admin-platform-scheduler-card">
      <div className="flex items-start gap-3 mb-3">
        <Clock className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            Hintergrund-Jobs
            <Badge variant="outline" className={`text-[10px] ${st.mode === "platform" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`} data-testid="scheduler-mode-badge">
              {st.mode === "platform" ? "Plattform-Cron (ausserhalb Webprozess)" : "In-Prozess-Scheduler"}
            </Badge>
          </h3>
          <p className="text-xs text-slate-500">
            {st.jobs_total} Jobs registriert · Plattform-Cron ruft alle {st.tick_minutes} Min <code>/api/cron/tick</code> auf und führt fällige Jobs aus · aktuell fällig: {st.due_now}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="scheduler-refresh"><RefreshCw className="w-3 h-3" /></Button>
      </div>

      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="py-1">Job</th><th>Zeitplan</th><th>Letzter Lauf</th><th>Nächster</th><th></th></tr></thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.job_id} className="border-b border-slate-50" data-testid={`scheduler-job-${j.job_id}`}>
              <td className="py-1 font-mono text-[11px] text-slate-800">{j.job_id}</td>
              <td className="text-slate-500 text-[11px] max-w-[220px] truncate" title={j.trigger}>{j.trigger.replace("cron[", "").replace("interval[", "alle ").replace("]", "")}</td>
              <td className="text-[11px]">
                <span className="inline-flex items-center gap-1">
                  {j.last_fire ? (j.last_ok === false ? <AlertTriangle className="w-3 h-3 text-rose-600" /> : <CheckCircle2 className="w-3 h-3 text-emerald-600" />) : null}
                  {fmt(j.last_fire)}{j.last_duration_s != null ? ` · ${j.last_duration_s}s` : ""}
                </span>
                {j.last_error && <div className="text-rose-700 truncate max-w-[220px]" title={j.last_error}>{j.last_error}</div>}
              </td>
              <td className={`text-[11px] ${j.due_now ? "text-amber-700 font-medium" : "text-slate-600"}`}>{fmt(j.next_due)}{j.due_now ? " · fällig" : ""}</td>
              <td className="text-right">
                <Button size="sm" variant="ghost" onClick={() => runJob(j.job_id)} disabled={!!running} title="Jetzt ausführen" data-testid={`scheduler-run-${j.job_id}`}>
                  {running === j.job_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {st.jobs.length > 8 && (
        <button type="button" className="mt-2 text-xs text-indigo-600 hover:underline" onClick={() => setShowAll((v) => !v)} data-testid="scheduler-show-all">
          {showAll ? "Weniger anzeigen" : `Alle ${st.jobs.length} Jobs anzeigen`}
        </button>
      )}
      {st.recent_ticks?.length > 0 && (
        <div className="mt-3 text-[11px] text-slate-500" data-testid="scheduler-recent-ticks">
          Letzte Ticks: {st.recent_ticks.slice(0, 5).map((r) => `${fmt(r.started_at)} (${r.jobs_run ?? 0} Jobs${r.failed ? `, ${r.failed} Fehler` : ""})`).join(" · ")}
        </div>
      )}
    </Card>
  );
}
